/**
 * /hr/email — Email connector settings page.
 *
 * Wired to live endpoints:
 *   GET  /api/v1/org-config       — full org config (provider, toggle, FROM name, reply-to)
 *   PATCH /api/v1/org-config      — save email_enabled, email_from_name, email_reply_to
 *   POST /api/v1/hr/email/test    — send a test email; returns { delivered: bool }
 */

import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card } from '../components/primitives/Card'
import { PageHead } from '../components/primitives/PageHead'
import { HrAccessGuard } from '../components/hr/HrAccessGuard'
import { getOrgConfig, sendTestEmailHr, updateOrgConfig } from '../api/org-config'

// ── Page shell ────────────────────────────────────────────────────────────────

export function HrEmailPage(): JSX.Element {
  return (
    <HrAccessGuard>
      <div className="page">
        <EmailPageInner />
      </div>
    </HrAccessGuard>
  )
}

// ── Inner page (always HR-gated) ─────────────────────────────────────────────

function EmailPageInner(): JSX.Element {
  const queryClient = useQueryClient()

  const configQuery = useQuery({
    queryKey: ['org-config', 'full'],
    queryFn: getOrgConfig,
    retry: false,
  })

  const cfg = configQuery.data

  // Editable local state — seeded from server once config loads.
  const [emailEnabled, setEmailEnabled] = useState(false)
  const [emailFromName, setEmailFromName] = useState('')
  const [emailReplyTo, setEmailReplyTo] = useState('')
  const [testTo, setTestTo] = useState('')
  const [saved, setSaved] = useState(false)
  const [testResult, setTestResult] = useState<boolean | null>(null)

  useEffect(() => {
    if (cfg) {
      setEmailEnabled(cfg.emailEnabled)
      setEmailFromName(cfg.emailFromName ?? '')
      setEmailReplyTo(cfg.emailReplyTo ?? '')
    }
  }, [cfg])

  const saveMutation = useMutation({
    mutationFn: () =>
      updateOrgConfig({
        emailEnabled,
        emailFromName: emailFromName || null,
        emailReplyTo: emailReplyTo || null,
      }),
    onSuccess: () => {
      setSaved(true)
      void queryClient.invalidateQueries({ queryKey: ['org-config'] })
    },
  })

  const testMutation = useMutation({
    mutationFn: () => sendTestEmailHr(testTo.trim() || undefined),
    onSuccess: (data) => {
      setTestResult(data.delivered)
    },
    onError: () => {
      setTestResult(false)
    },
  })

  const providerLabel: string = cfg?.emailProvider || 'none'

  return (
    <>
      <PageHead
        eyebrow="HR · Integrations"
        title="Email connector"
        subtitle="Outbound email notifications for leave lifecycle events."
      />

      {/* ── Config card ───────────────────────────────────────── */}
      <Card padding={0} style={{ marginBottom: 16 }}>
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 15 }}>Configuration</div>
          <div style={{ color: 'var(--text-3)', fontSize: 13, marginTop: 2 }}>
            Provider credentials are set at the deployment level — not editable here.
          </div>
        </div>

        {configQuery.isLoading && (
          <div style={{ padding: '24px 22px', fontSize: 13, color: 'var(--text-3)' }}>
            Loading…
          </div>
        )}

        {configQuery.isError && (
          <div
            role="alert"
            style={{ padding: '16px 22px', fontSize: 13, color: 'var(--danger)' }}
          >
            Failed to load email configuration.
          </div>
        )}

        {cfg && (
          <div style={{ padding: '18px 22px', display: 'grid', gap: 16 }}>
            {/* Provider (read-only) */}
            <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
              <span style={{ color: 'var(--text-3)' }}>Provider: </span>
              <strong style={{ color: 'var(--text)' }}>{providerLabel}</strong>
              <span style={{ color: 'var(--text-3)', marginLeft: 8 }}>
                (set at the deployment level — not editable here)
              </span>
            </div>

            {/* Email notifications toggle */}
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={emailEnabled}
                onChange={(e) => {
                  setEmailEnabled(e.target.checked)
                  setSaved(false)
                }}
                style={{ width: 16, height: 16, cursor: 'pointer' }}
              />
              <span style={{ fontSize: 14, color: 'var(--text)', fontWeight: 500 }}>
                Email notifications enabled
              </span>
            </label>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: -8, marginLeft: 26 }}>
              Employees receive submission, decision, cancellation and escalation emails.
              Needs a configured provider above.
            </div>

            {/* FROM display name */}
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 13, color: 'var(--text-2)', fontWeight: 500 }}>
                FROM display name
              </span>
              <input
                type="text"
                value={emailFromName}
                maxLength={120}
                placeholder="e.g. Leave Hub"
                aria-label="FROM display name"
                style={inputStyle}
                onChange={(e) => {
                  setEmailFromName(e.target.value)
                  setSaved(false)
                }}
              />
            </label>

            {/* Reply-to address */}
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 13, color: 'var(--text-2)', fontWeight: 500 }}>
                Reply-to address
              </span>
              <input
                type="email"
                value={emailReplyTo}
                maxLength={200}
                placeholder="e.g. hr@company.com"
                aria-label="Reply-to email address"
                style={inputStyle}
                onChange={(e) => {
                  setEmailReplyTo(e.target.value)
                  setSaved(false)
                }}
              />
            </label>

            {/* Save */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <button
                type="button"
                disabled={saveMutation.isPending}
                onClick={() => { setSaved(false); saveMutation.mutate() }}
                style={buttonStylePrimary}
              >
                {saveMutation.isPending ? 'Saving…' : 'Save'}
              </button>
              <span
                style={{
                  fontSize: 13,
                  color: 'var(--success)',
                  opacity: saved ? 1 : 0,
                  transition: 'opacity 150ms ease',
                }}
                aria-live="polite"
              >
                Saved
              </span>
              {saveMutation.isError && (
                <span style={{ fontSize: 13, color: 'var(--danger)' }}>
                  Save failed
                </span>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* ── Send test email card ─────────────────────────────── */}
      <Card padding={0} style={{ marginBottom: 16 }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>Send test email</div>
          <div style={{ color: 'var(--text-3)', fontSize: 13, marginTop: 2 }}>
            Verify that the configured provider can deliver outbound messages.
          </div>
        </div>
        <div style={{ padding: '18px 22px', display: 'grid', gap: 14 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 13, color: 'var(--text-2)', fontWeight: 500 }}>
              Send test to
            </span>
            <input
              type="email"
              value={testTo}
              placeholder="you@company.com"
              aria-label="Send test email to address"
              style={inputStyle}
              onChange={(e) => {
                setTestTo(e.target.value)
                setTestResult(null)
              }}
            />
            <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
              Leave empty to send to your own account.
            </span>
          </label>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              disabled={testMutation.isPending}
              onClick={() => {
                setTestResult(null)
                testMutation.mutate()
              }}
              style={buttonStyleSoft}
              aria-label="Send test email"
            >
              {testMutation.isPending ? 'Sending…' : 'Send test email'}
            </button>

            {/* Result line — animated show/hide */}
            <div
              aria-live="polite"
              style={{
                fontSize: 13,
                opacity: testResult !== null ? 1 : 0,
                transform: testResult !== null ? 'translateY(0)' : 'translateY(4px)',
                transition: 'opacity 150ms ease, transform 150ms ease',
                color: testResult === true ? 'var(--success)' : 'var(--danger)',
              }}
            >
              {testResult === true
                ? 'Delivered — check your email to confirm.'
                : testResult === false
                  ? 'Not delivered — check provider config.'
                  : null}
            </div>
          </div>
        </div>
      </Card>
    </>
  )
}

// ── Style constants ───────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  fontSize: 13,
  padding: '0 10px',
  borderRadius: 6,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontFamily: 'inherit',
  minHeight: 44,
  boxSizing: 'border-box',
  width: '100%',
  maxWidth: 420,
}

const buttonStylePrimary: React.CSSProperties = {
  padding: '7px 16px',
  borderRadius: 8,
  border: 'none',
  background: 'var(--steel-blue)',
  color: 'var(--surface)',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
  minHeight: 44,
}

const buttonStyleSoft: React.CSSProperties = {
  padding: '7px 16px',
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'transparent',
  color: 'var(--text-2)',
  fontWeight: 500,
  fontSize: 13,
  cursor: 'pointer',
  minHeight: 44,
}
